import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import '../styles/TermsAndConditions.css';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO 
        title="Terms and Conditions | BookMyCamera"
        description="Read our Terms and Conditions to understand the rules and regulations for using BookMyCamera services."
      />
      <div className="terms-container">
        <div className="terms-header">
          <button 
            type="button"
            className="terms-back-btn" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // If opened in new tab/window, try to close it
              if (window.opener) {
                try {
                  window.close();
                } catch (err) {
                  navigate('/');
                }
              } else {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/');
                }
              }
            }}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <h1>Terms and Conditions</h1>
          <p className="terms-last-updated">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="terms-content">
          <section className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using BookMyCamera, you accept and agree to be bound by the terms and provision of this agreement. 
              If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Use License</h2>
            <p>
              Permission is granted to temporarily access the materials on BookMyCamera's website for personal, non-commercial 
              transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
            </p>
            <ul>
              <li>Modify or copy the materials</li>
              <li>Use the materials for any commercial purpose or for any public display</li>
              <li>Attempt to reverse engineer any software contained on BookMyCamera's website</li>
              <li>Remove any copyright or other proprietary notations from the materials</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>3. Equipment Rental</h2>
            <p>
              All equipment rented through BookMyCamera remains the property of BookMyCamera or its partners. Renters are 
              responsible for the care and return of equipment in the same condition as received, normal wear and tear excepted.
            </p>
            <p>
              Renters will be held financially responsible for any damage, loss, or theft of equipment during the rental period.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. Booking and Payment</h2>
            <p>
              All bookings are subject to availability and confirmation. Payment must be made in full at the time of booking 
              unless otherwise agreed upon. BookMyCamera reserves the right to cancel any booking if payment is not received 
              or if there are issues with the payment method.
            </p>
            <p>
              Refunds and cancellations are subject to our cancellation policy, which will be communicated at the time of booking.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. User Accounts</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account and password. You agree to accept 
              responsibility for all activities that occur under your account or password.
            </p>
            <p>
              You must provide accurate, current, and complete information during the registration process and keep your 
              account information updated.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Prohibited Uses</h2>
            <p>You may not use our service:</p>
            <ul>
              <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
              <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
              <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
              <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
              <li>To submit false or misleading information</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>7. Limitation of Liability</h2>
            <p>
              In no event shall BookMyCamera, nor its directors, employees, partners, agents, suppliers, or affiliates, be 
              liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, 
              loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless BookMyCamera and its licensee and licensors, and their employees, 
              contractors, agents, officers and directors, from and against any and all claims, damages, obligations, losses, 
              liabilities, costs or debt, and expenses (including but not limited to attorney's fees).
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Termination</h2>
            <p>
              We may terminate or suspend your account and bar access to the service immediately, without prior notice or 
              liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited 
              to a breach of the Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Changes to Terms</h2>
            <p>
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is 
              material, we will provide at least 30 days notice prior to any new terms taking effect.
            </p>
            <p>
              What constitutes a material change will be determined at our sole discretion.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Contact Information</h2>
            <p>
              If you have any questions about these Terms and Conditions, please contact us at:
            </p>
            <p>
              <strong>Email:</strong> {process.env.REACT_APP_EMAIL || 'bookmycamera@gmail.com'}<br />
              <strong>Phone:</strong> +91 {process.env.REACT_APP_PHONE_NUMBER || ''}<br />
              <strong>Address:</strong> {process.env.REACT_APP_ADDRESS || '23, 12th Main Rd, 4th Block East, KV Layout, Jayanagar, Bengaluru, Karnataka 560011'}
            </p>
          </section>
        </div>

        <div className="terms-footer">
          <button 
            className="terms-accept-btn" 
            onClick={() => {
              // Store that user accepted terms from terms page
              localStorage.setItem('termsAcceptedFromPage', 'true');
              
              // Trigger storage event for other tabs/windows
              window.dispatchEvent(new Event('storage'));
              
              // Try to close if opened in new tab, otherwise navigate back
              if (window.opener) {
                // Opened in new window/tab
                window.close();
              } else {
                // Same tab - navigate back or to home
                navigate('/');
              }
            }}
          >
            I Understand
          </button>
        </div>
      </div>
    </>
  );
};

export default TermsAndConditions;

